import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ShoppingBag, ArrowRight, Loader2 } from 'lucide-react';
import Header from '../components/Layout/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

export default function PaymentTemplateSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [templateId, setTemplateId] = useState(null);

  useEffect(() => {
    const verify = async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token || !sessionId) {
        setStatus('success');
        return;
      }

      // Poll up to 5 times — webhook may take a moment
      for (let i = 0; i < 5; i++) {
        try {
          const r = await fetch(`${API_URL}/api/payments/verify-template-purchase?session_id=${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.ok) {
            const d = await r.json();
            setTemplateId(d.template_id || null);
            setStatus('success');
            return;
          }
        } catch (_) {}

        if (i < 4) await new Promise((res) => setTimeout(res, 1500));
      }

      // Show success even if verification polling times out
      setStatus('success');
    };

    verify();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-violet-50 flex flex-col">
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-10 text-center">
          {status === 'loading' ? (
            <>
              <Loader2 className="w-12 h-12 text-violet-500 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Confirming your purchase…</h2>
              <p className="text-sm text-gray-500">This usually takes just a moment.</p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Purchase Successful!</h2>
              <p className="text-gray-500 mb-8">
                Your template is ready to use. Open it in the editor and start designing.
              </p>
              <div className="space-y-3">
                {templateId && (
                  <button
                    onClick={() => navigate(`/marketplace/templates/${templateId}`)}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow"
                  >
                    <ShoppingBag className="w-4 h-4" />
                    View Template
                  </button>
                )}
                <button
                  onClick={() => navigate('/marketplace')}
                  className="w-full py-3 rounded-xl border border-gray-200 text-gray-700 font-medium flex items-center justify-center gap-2 hover:border-violet-400 hover:text-violet-700 transition-all"
                >
                  Browse More Templates
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
